import { getApiService } from "./api";
import { z } from "zod";

import { UseCaseBriefSchema } from "../entities/useCaseChat/schema/UseCaseBrief.schema";
import type { UseCaseBrief } from "../entities/useCaseChat/UseCaseBrief";
import { UseCaseChatSchema } from "../entities/useCaseChat/schema/UseCaseChat.schema";
import { UseCaseChat } from "../entities/useCaseChat/UseCaseChat";
import type { UseCase } from "../entities/useCaseChat/UseCase";
import type { License } from "../entities/user/License";
import {
  ImpactPlusReportSchema,
  type ImpactPlusReport,
} from "../entities/report/schemas/ImpactPlusReport.schema";

class UseCaseService {
  async getUseCases(): Promise<UseCaseBrief[]> {
    const apiService = getApiService();
    const response = await apiService.getParsed("/dashboard/my-use-cases", { useCases: z.array(UseCaseBriefSchema) });
    if (response.status !== "success" || !response.useCases) {
      throw new Error(`Failed to fetch use cases: ${response.message || "Server error"}`);
    }

    return response.useCases;
  }

  async getUseCaseById(id: string): Promise<UseCaseChat> {
    const apiService = getApiService();
    try {

      const response = await apiService.getParsed(`/use-case-chat/${id}`, { useCaseChat: UseCaseChatSchema })

      if (response.status !== "success" || !response.useCaseChat) {
        throw new Error(`Failed to fetch use case with id ${id}`);
      }

      return response.useCaseChat;
    } catch (error) {
      console.error(`Error fetching use case with id ${id}:`, error);
      throw error;
    }
  }

  async createUseCase(): Promise<{ useCaseChatId: string }> {
    const apiService = getApiService();

    try {
      const response = await apiService.post<{
        status: string;
        message?: string;
        useCaseChatId: string;
      }>("/use-case-chat/init");

      if (response.status !== "success" || !response.useCaseChatId) {
        throw new Error(
          `Failed to create use case: ${response?.message || "Server error"}`,
        );
      }

      return { useCaseChatId: response.useCaseChatId };
    } catch (error) {
      console.error("Error creating use case:", error);
      throw error;
    }
  }

  async updateUseCase(
    id: string,
    useCaseData: Partial<UseCase>,
  ): Promise<UseCaseChat> {
    const apiService = getApiService();

    try {
      const response = await apiService.putParsed(
        `/use-case-chat/${id}/patch-manual`,
        useCaseData,
        { useCaseChat: UseCaseChatSchema }
      );

      if (response.status !== "success" || !response.useCaseChat) {
        throw new Error(`Failed to update use case with id ${id}: ${response.message || "Server error"}`);
      }

      return response.useCaseChat;
    } catch (error) {
      console.error(`Error updating use case with id ${id}:`, error);
      throw error;
    }
  }

  async deleteUseCase(id: string): Promise<boolean> {

    const apiService = getApiService();
    try {
      await apiService.delete<void>(`/use-case-chat/${id}`);
      return true;
    } catch (error) {
      console.error(`Error deleting use case with id ${id}:`, error);
      throw error;
    }
  }



  async generateImpactPlusReport(useCaseChatId: string): Promise<{ jobId: string; license: Partial<License> }> {
    const apiService = getApiService();
    try {
      const response = await apiService.postParsed(
        `/impact-plus-report/create`,
        { useCaseChatId },
        {
          jobId: z.string(),
          license: z.object({
            impactPlusReportCredits: z.number().optional().default(0),
          }),
        },
      );

      if (response.status !== 'success' || !response.jobId) {
        throw new Error(
          `Failed to generate Impact+ report for use case ${useCaseChatId}: ${response.message || 'Server error'}`,
        );
      }

      return { jobId: response.jobId, license: response.license };
    } catch (error) {
      console.error(`Error generating Impact+ report for use case ${useCaseChatId}:`, error);
      throw error;
    }
  }

  async getImpactPlusReport(impactPlusReportId: string): Promise<ImpactPlusReport> {
    const apiService = getApiService();
    try {
      const response = await apiService.getParsed(
        `/impact-plus-report/${impactPlusReportId}`,
        { impactPlusReport: ImpactPlusReportSchema },
      );

      if (response.status !== "success" || !response.impactPlusReport) {
        throw new Error(
          `Failed to fetch Impact+ report ${impactPlusReportId}: ${response.message || "Server error"}`,
        );
      }

      return response.impactPlusReport;
    } catch (error) {
      console.error(
        `Error fetching Impact+ report ${impactPlusReportId}:`,
        error,
      );
      throw error;
    }
  }

  async getUseCaseChatReports(useCaseChatId: string): Promise<{
    impactPlusReport: { reportType: string; reportTitle: string; reportId: string } | null;
    riskPlusReport: { reportType: string; reportTitle: string; reportId: string } | null;
  }> {
    const apiService = getApiService();
    const ReportItemSchema = z.object({
      reportType: z.string(),
      reportTitle: z.string(),
      reportId: z.string(),
    });
    const response = await apiService.getParsed(
      `/reports/use-case-chat/${useCaseChatId}`,
      {
        reports: z.object({
          impactPlusReport: ReportItemSchema.nullable().optional(),
          riskPlusReport: ReportItemSchema.nullable().optional(),
        }),
      },
    );

    if (response.status !== 'success' || !response.reports) {
      throw new Error(`Failed to fetch reports for use case ${useCaseChatId}: ${response.message || 'Server error'}`);
    }

    return {
      impactPlusReport: response.reports.impactPlusReport ?? null,
      riskPlusReport: response.reports.riskPlusReport ?? null,
    };
  }

  async sendMessage(
    id: string,
    messageContent: string,
  ): Promise<UseCaseChat> {
    const apiService = getApiService();

    try {
      const response = await apiService.postParsed(
        `/use-case-chat/${id}/sendMessage`,
        {
          messageContent,
        },
        {
          useCaseChat: UseCaseChatSchema
        },
        { timeout: 120_000 }
      );

      if (response.status !== "success" || !response.useCaseChat) {
        throw new Error(`Failed to send message for use case with id ${id}: ${response.message || "Server error"}`);
      }
      return response.useCaseChat;

    } catch (error) {
      console.error(`Error sending message for use case with id ${id}:`, error);
      throw error;
    }
  }



}

//export const useCaseService = new UseCaseService();

let _useCaseServiceInstance = new UseCaseService();

export function getUseCaseService(): UseCaseService {
  if (!_useCaseServiceInstance) {
    _useCaseServiceInstance = new UseCaseService();
  }
  return _useCaseServiceInstance;
}


